import { Link, useNavigate } from 'react-router-dom'
import { Plus, ClipboardList, Boxes } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { NodeFileTypeIcon } from '@/components/nodes/NodeFileTypeIcon'
import { Project, Workspace, Document, Task, FileType } from '@/types'
import { LucideIcon } from 'lucide-react'

// Type for nodes that can be displayed
type AssociatedNode = Project | Workspace | Document | Task

// Helper to check if an node has a description
function hasDescription(node: AssociatedNode): node is Project | Document | Task {
  return 'description' in node && node.description !== undefined
}

// Helper to check if an node is a document (has a FileType)
function isDocument(node: AssociatedNode): node is Document {
  return (
    'type' in node &&
    typeof node.type === 'string' &&
    Object.values(FileType).includes(node.type as FileType)
  )
}

// Helper to check if an node is a project
function isProject(node: AssociatedNode): node is Project {
  return 'description' in node && !isDocument(node)
}

// Helper to check if an node is a task
function isTask(node: AssociatedNode): node is Task {
  return 'status' in node && 'priority' in node
}

// Helper to render the appropriate icon for an node
function getNodeIcon(node: AssociatedNode) {
  if (isDocument(node)) {
    return <NodeFileTypeIcon type={node.type} className="h-3.5 w-3.5 flex-shrink-0" />
  } else if (isTask(node)) {
    return <ClipboardList className="h-3.5 w-3.5 flex-shrink-0" />
  } else if (isProject(node)) {
    return <ClipboardList className="h-3.5 w-3.5 flex-shrink-0" />
  } else {
    // Workspace
    return <Boxes className="h-3.5 w-3.5 flex-shrink-0" />
  }
}

interface NodeAssociationsProps {
  /** Array of nodes to display */
  nodes: AssociatedNode[]
  /** Type of node being displayed - determines URL pattern and terminology */
  nodeType: 'projects' | 'workspaces' | 'documents' | 'tasks'
  /** Maximum number of nodes to show before "+X more" indicator */
  maxVisible?: number
  /** URL to navigate to when "View all" is clicked */
  viewAllUrl?: string
  /** Label for the "View all" button */
  viewAllLabel?: string
  /** Whether to show the "View all" button */
  showViewAll?: boolean
  /** Additional CSS classes for the container */
  className?: string
  /** Custom empty state message when no nodes */
  emptyState?: React.ReactNode
  /** Icon component to display before the node list */
  icon?: LucideIcon
  /** Label text to display before the node list */
  label?: string
  /** Display mode for nodes - "badge" (default), "text" for simple text display, or "card" for vertical card layout */
  displayMode?: 'badge' | 'text' | 'card'
  /** Whether to stack the label above the nodes list */
  labelStacked?: boolean
  /** Whether to stack the nodes vertically instead of horizontally */
  listStacked?: boolean
  /** Link text color - "default" for text-foreground or "muted" for text-muted-foreground */
  linkColor?: 'default' | 'muted'
  /** Whether to display the node icon next to the node name */
  listIcon?: boolean
}

/**
 * NodeAssociations Component
 *
 * Displays a list of associated nodes for any node
 * as clickable badges, text, or cards with tooltips showing node details.
 *
 * @example
 * ```tsx
 * // Projects for a task (badge display)
 * <NodeAssociations
 *   nodes={task.projects}
 *   nodeType="projects"
 *   maxVisible={5}
 *   viewAllUrl={`/tasks/${task.id}/projects`}
 *   viewAllLabel="View all projects"
 * />
 *
 * // Workspaces for a user
 * <NodeAssociations
 *   nodes={user.workspaces}
 *   nodeType="workspaces"
 *   maxVisible={3}
 *   showViewAll={false}
 *   icon={Boxes}
 *   label="Workspaces:"
 * />
 *
 * // Documents with card display mode
 * <NodeAssociations
 *   nodes={task.documents}
 *   nodeType="documents"
 *   maxVisible={5}
 *   displayMode="card"
 *   showViewAll={false}
 *   icon={FileText}
 *   label="Documents:"
 * />
 *
 * // Simple text display mode (for task list items)
 * <NodeAssociations
 *   nodes={[task.project]}
 *   nodeType="projects"
 *   maxVisible={1}
 *   displayMode="text"
 *   showViewAll={false}
 * />
 *
 * // Stacked label display
 * <NodeAssociations
 *   nodes={user.projects}
 *   nodeType="projects"
 *   label="Projects:"
 *   labelStacked={true}
 *   maxVisible={5}
 * />
 *
 * // With icons displayed next to node names
 * <NodeAssociations
 *   nodes={task.documents}
 *   nodeType="documents"
 *   displayMode="badge"
 *   listIcon={true}
 *   maxVisible={5}
 * />
 *
 * // With vertically stacked associations list
 * <NodeAssociations
 *   nodes={task.projects}
 *   nodeType="projects"
 *   listStacked={true}
 *   maxVisible={5}
 * />
 * ```
 */
export function NodeAssociations({
  nodes,
  nodeType,
  maxVisible = 5,
  viewAllUrl,
  viewAllLabel = 'View all',
  showViewAll = true,
  className = '',
  emptyState,
  icon: Icon,
  label,
  displayMode = 'badge',
  labelStacked = false,
  listStacked = false,
  linkColor = 'default',
  listIcon = false,
}: NodeAssociationsProps) {
  const navigate = useNavigate()
  const visibleNodes = nodes.slice(0, maxVisible)
  const hiddenCount = nodes.length - maxVisible
  const linkColorClass = linkColor === 'muted' ? 'text-muted-foreground' : 'text-foreground'

  // Handle node click with event propagation prevention
  const handleNodeClick = (e: React.MouseEvent, nodeId: string) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/${nodeType}/${nodeId}`)
  }

  // Generate node type label for default messages
  const nodeTypeLabel =
    nodeType === 'projects'
      ? 'projects'
      : nodeType === 'workspaces'
        ? 'workspaces'
        : nodeType === 'documents'
          ? 'documents'
          : 'tasks'
  const defaultLabel =
    label ??
    `${nodeType === 'projects' ? 'Projects' : nodeType === 'workspaces' ? 'Workspaces' : nodeType === 'documents' ? 'Documents' : 'Tasks'}:`

  if (nodes.length === 0) {
    return (
      <div
        className={`flex ${labelStacked ? 'flex-col items-start' : 'items-center'} gap-2 ${className}`}
      >
        {(Icon || label) && (
          <div className="flex items-center gap-1">
            {Icon && <Icon className="h-4 w-4" />}
            <span className="text-sm text-muted-foreground">{defaultLabel}</span>
          </div>
        )}
        {emptyState || (
          <div className="text-sm text-muted-foreground">
            No {nodeTypeLabel} associated with this node
          </div>
        )}
      </div>
    )
  }

  // Card display mode (vertical layout for documents)
  if (displayMode === 'card') {
    return (
      <TooltipProvider>
        <div className={`space-y-2 ${className}`}>
          {(Icon || label) && (
            <div className="flex items-center gap-1">
              {Icon && <Icon className="h-4 w-4" />}
              <span className="text-sm text-muted-foreground">{defaultLabel}</span>
            </div>
          )}
          <div className="space-y-2">
            {visibleNodes.map((node) => (
              <div
                key={node.id}
                onClick={(e) => handleNodeClick(e, node.id)}
                className="flex items-center gap-2 p-2 rounded-lg border hover:bg-accent transition-colors cursor-pointer"
              >
                {listIcon && <div className="flex-shrink-0">{getNodeIcon(node)}</div>}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${linkColorClass}`}>{node.name}</p>
                  {hasDescription(node) && node.description && (
                    <p className="text-xs text-muted-foreground truncate">{node.description}</p>
                  )}
                  {isDocument(node) && (
                    <p className="text-xs text-muted-foreground mt-1">{node.type}</p>
                  )}
                  {isTask(node) && (
                    <>
                      <p className="text-xs text-muted-foreground mt-1">{node.status}</p>
                      {node.priority && (
                        <p className="text-xs text-muted-foreground">Priority: {node.priority}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}

            {hiddenCount > 0 && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 border">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground">
                    +{hiddenCount} more{' '}
                    {hiddenCount === 1 ? nodeTypeLabel.slice(0, -1) : nodeTypeLabel}
                  </p>
                </div>
              </div>
            )}
          </div>

          {showViewAll && nodes.length > 0 && viewAllUrl && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" asChild>
              <Link to={viewAllUrl}>{viewAllLabel}</Link>
            </Button>
          )}

          {showViewAll && !viewAllUrl && nodes.length > maxVisible && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  View all {nodes.length} {nodeTypeLabel}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>
    )
  }

  // Badge/Text display mode (horizontal layout)
  return (
    <TooltipProvider>
      <div
        className={`flex ${labelStacked ? 'flex-col items-start' : 'items-center'} gap-2 ${className}`}
      >
        {(Icon || label) && (
          <div className="flex items-center gap-1">
            {Icon && <Icon className="h-4 w-4" />}
            <span className="text-sm text-muted-foreground">{defaultLabel}</span>
          </div>
        )}
        <div
          className={`flex ${listStacked ? 'flex-col items-start' : 'items-center flex-wrap'} gap-2`}
        >
          {visibleNodes.map((node) => (
            <Tooltip key={node.id}>
              <TooltipTrigger asChild>
                <div onClick={(e) => handleNodeClick(e, node.id)}>
                  {displayMode === 'text' ? (
                    <span
                      className={`flex items-center gap-1 text-xs ${linkColorClass} hover:${linkColorClass} hover:underline cursor-pointer`}
                    >
                      {listIcon && getNodeIcon(node)}
                      <span className="truncate leading-none">{node.name}</span>
                    </span>
                  ) : (
                    <Badge
                      variant="outline"
                      className={`hover:bg-accent cursor-pointer ${linkColorClass} ${listIcon ? 'flex items-center gap-1' : ''}`}
                    >
                      {listIcon && getNodeIcon(node)}
                      {node.name}
                    </Badge>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px]">
                <p>{node.name}</p>
                {hasDescription(node) && node.description && (
                  <p className="text-xs text-muted-foreground mt-1">{node.description}</p>
                )}
                {isDocument(node) && (
                  <p className="text-xs text-muted-foreground mt-1">{node.type}</p>
                )}
                {isTask(node) && (
                  <>
                    <p className="text-xs text-muted-foreground mt-1">{node.status}</p>
                    {node.priority && (
                      <p className="text-xs text-muted-foreground">Priority: {node.priority}</p>
                    )}
                  </>
                )}
              </TooltipContent>
            </Tooltip>
          ))}

          {hiddenCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                {displayMode === 'text' ? (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                    <span>+{hiddenCount} more</span>
                  </span>
                ) : (
                  <Badge variant="outline" className="hover:bg-accent cursor-pointer">
                    +{hiddenCount} more
                  </Badge>
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  View all {nodes.length} {nodeTypeLabel}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {showViewAll && nodes.length > 0 && viewAllUrl && (
          <Button variant="ghost" size="sm" className="h-7 text-xs -ml-1" asChild>
            <Link to={viewAllUrl}>{viewAllLabel}</Link>
          </Button>
        )}

        {showViewAll && !viewAllUrl && nodes.length > maxVisible && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 text-xs">
                <Plus className="h-4 w-4 mr-1" />
                View all
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                View all {nodes.length} {nodeTypeLabel}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  )
}
